import React, { useState, useRef, useEffect } from 'react';
import { 
  Image, Mic, Images, Move, Layers, Wand2, AudioLines,
  Upload, Play, Pause, Loader2, Check, X, Plus, Trash2,
  ChevronDown, Volume2, VolumeX, Clock, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// 1. IMAGE TO VIDEO COMPONENT
// ============================================================
export function ImageToVideo({ onComplete, onClose }) {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audio, setAudio] = useState(null);
  const [duration, setDuration] = useState(10);
  const [loading, setLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('auth_token');

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!image) {
      toast.error('Please select an image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', image);
      if (audio) formData.append('audio', audio);
      formData.append('duration', duration.toString());

      const response = await fetch(`${API_URL}/api/video-studio/image-to-video`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create video');
      }

      const result = await response.json();
      toast.success('Video created successfully!');
      onComplete?.(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg border border-blue-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Image className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-blue-300">Image to Video</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Create a video from a still image. Add audio to set the duration automatically.
        </p>
      </div>

      {/* Image Upload */}
      <div className="space-y-2">
        <Label className="text-white">Image</Label>
        <div 
          className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center cursor-pointer hover:border-zinc-500 transition-colors"
          onClick={() => document.getElementById('img2vid-image')?.click()}
        >
          {imagePreview ? (
            <img src={imagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
          ) : (
            <div className="py-8">
              <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
              <p className="text-sm text-zinc-400">Click to upload image</p>
            </div>
          )}
        </div>
        <input
          id="img2vid-image"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageChange}
        />
      </div>

      {/* Audio Upload (Optional) */}
      <div className="space-y-2">
        <Label className="text-white">Audio (Optional)</Label>
        <div className="flex items-center gap-2">
          <Input
            type="file"
            accept="audio/*"
            onChange={(e) => setAudio(e.target.files?.[0] || null)}
            className="bg-zinc-800 border-zinc-700"
          />
          {audio && (
            <Button variant="ghost" size="icon" onClick={() => setAudio(null)}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <p className="text-xs text-zinc-500">
          {audio ? `Audio: ${audio.name}` : 'If provided, video duration matches audio length'}
        </p>
      </div>

      {/* Duration (if no audio) */}
      {!audio && (
        <div className="space-y-2">
          <Label className="text-white">Duration: {duration}s</Label>
          <Slider
            value={[duration]}
            onValueChange={(v) => setDuration(v[0])}
            min={3}
            max={60}
            step={1}
            className="w-full"
          />
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={loading || !image}
        className="w-full bg-gradient-to-r from-blue-500 to-cyan-500"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
        Create Video
      </Button>
    </div>
  );
}

// ============================================================
// 2. VOICEOVER RECORDER COMPONENT
// ============================================================
export function VoiceoverRecorder({ videoUrl, onComplete, onClose }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [originalVolume, setOriginalVolume] = useState(0.3);
  const [voiceoverVolume, setVoiceoverVolume] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const getAuthToken = () => localStorage.getItem('auth_token');

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      toast.info('Recording started...');
    } catch (error) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      toast.success('Recording stopped');
    }
  };

  const handleSubmit = async () => {
    const audioFile = uploadedAudio || (audioBlob ? new File([audioBlob], 'voiceover.webm') : null);
    
    if (!audioFile) {
      toast.error('Please record or upload a voiceover');
      return;
    }

    setLoading(true);
    try {
      // First fetch the video
      const videoResponse = await fetch(videoUrl);
      const videoBlob = await videoResponse.blob();
      const videoFile = new File([videoBlob], 'video.mp4', { type: 'video/mp4' });

      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('voiceover', audioFile);
      formData.append('original_volume', originalVolume.toString());
      formData.append('voiceover_volume', voiceoverVolume.toString());

      const response = await fetch(`${API_URL}/api/video-studio/add-voiceover`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add voiceover');
      }

      const result = await response.json();
      toast.success('Voiceover added successfully!');
      onComplete?.(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-gradient-to-r from-red-500/10 to-pink-500/10 rounded-lg border border-red-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Mic className="w-4 h-4 text-red-400" />
          <span className="text-sm font-medium text-red-300">Record Voiceover</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Record or upload a voiceover to add narration to your video.
        </p>
      </div>

      {/* Record Controls */}
      <div className="flex gap-2">
        <Button
          onClick={recording ? stopRecording : startRecording}
          variant={recording ? "destructive" : "outline"}
          className="flex-1"
        >
          {recording ? (
            <>
              <Pause className="w-4 h-4 mr-2" />
              Stop Recording
            </>
          ) : (
            <>
              <Mic className="w-4 h-4 mr-2" />
              Start Recording
            </>
          )}
        </Button>
      </div>

      {/* Or Upload */}
      <div className="text-center text-xs text-zinc-500">— or upload audio file —</div>
      <Input
        type="file"
        accept="audio/*"
        onChange={(e) => {
          setUploadedAudio(e.target.files?.[0] || null);
          setAudioBlob(null);
          setAudioUrl(null);
        }}
        className="bg-zinc-800 border-zinc-700"
      />

      {/* Preview */}
      {(audioUrl || uploadedAudio) && (
        <div className="p-3 bg-zinc-800 rounded-lg">
          <p className="text-xs text-zinc-400 mb-2">Preview:</p>
          <audio 
            src={audioUrl || (uploadedAudio ? URL.createObjectURL(uploadedAudio) : '')} 
            controls 
            className="w-full"
          />
        </div>
      )}

      {/* Volume Controls */}
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-white text-sm">Original Audio: {Math.round(originalVolume * 100)}%</Label>
          <Slider
            value={[originalVolume]}
            onValueChange={(v) => setOriginalVolume(v[0])}
            min={0}
            max={1}
            step={0.1}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-white text-sm">Voiceover: {Math.round(voiceoverVolume * 100)}%</Label>
          <Slider
            value={[voiceoverVolume]}
            onValueChange={(v) => setVoiceoverVolume(v[0])}
            min={0}
            max={2}
            step={0.1}
          />
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={loading || (!audioBlob && !uploadedAudio)}
        className="w-full bg-gradient-to-r from-red-500 to-pink-500"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
        Add Voiceover
      </Button>
    </div>
  );
}

// ============================================================
// 3. SLIDESHOW BUILDER COMPONENT
// ============================================================
export function SlideshowBuilder({ onComplete, onClose }) {
  const [images, setImages] = useState([]);
  const [durationPerImage, setDurationPerImage] = useState(3);
  const [transition, setTransition] = useState('fade');
  const [transitionDuration, setTransitionDuration] = useState(1);
  const [musicUrl, setMusicUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('auth_token');

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Upload images to get URLs
    const uploadedUrls = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('duration', '1');

      // Use a simple upload endpoint or convert to data URL
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      uploadedUrls.push({
        url: dataUrl,
        name: file.name,
        preview: URL.createObjectURL(file)
      });
    }
    
    setImages([...images, ...uploadedUrls]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (images.length < 2) {
      toast.error('Please add at least 2 images');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/video-studio/slideshow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          image_urls: images.map(img => img.url),
          duration_per_image: durationPerImage,
          transition: transition,
          transition_duration: transitionDuration,
          music_url: musicUrl || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create slideshow');
      }

      const result = await response.json();
      toast.success('Slideshow created successfully!');
      onComplete?.(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Images className="w-4 h-4 text-green-400" />
          <span className="text-sm font-medium text-green-300">Slideshow Builder</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Create a video slideshow from multiple images with transitions.
        </p>
      </div>

      {/* Image Grid */}
      <div className="space-y-2">
        <Label className="text-white">Images ({images.length}/50)</Label>
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, index) => (
            <div key={index} className="relative group">
              <img src={img.preview} alt="" className="w-full h-20 object-cover rounded" />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
              <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1 rounded">
                {index + 1}
              </span>
            </div>
          ))}
          <label className="border-2 border-dashed border-zinc-700 rounded flex items-center justify-center h-20 cursor-pointer hover:border-zinc-500">
            <Plus className="w-6 h-6 text-zinc-500" />
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageUpload}
            />
          </label>
        </div>
      </div>

      {/* Settings */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-white text-sm">Duration/Image: {durationPerImage}s</Label>
          <Slider
            value={[durationPerImage]}
            onValueChange={(v) => setDurationPerImage(v[0])}
            min={1}
            max={10}
            step={0.5}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-white text-sm">Transition</Label>
          <select
            value={transition}
            onChange={(e) => setTransition(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
          >
            <option value="fade">Fade</option>
            <option value="slide">Slide</option>
            <option value="zoom">Zoom</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={loading || images.length < 2}
        className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Images className="w-4 h-4 mr-2" />}
        Create Slideshow
      </Button>
    </div>
  );
}

// ============================================================
// 4. KEN BURNS EFFECT COMPONENT
// ============================================================
export function KenBurnsEffect({ onComplete, onClose }) {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audio, setAudio] = useState(null);
  const [duration, setDuration] = useState(10);
  const [effect, setEffect] = useState('zoom_in');
  const [intensity, setIntensity] = useState(0.3);
  const [loading, setLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('auth_token');

  const effects = [
    { value: 'zoom_in', label: 'Zoom In', desc: 'Start wide, zoom to center' },
    { value: 'zoom_out', label: 'Zoom Out', desc: 'Start close, pull back' },
    { value: 'pan_left', label: 'Pan Left', desc: 'Move from right to left' },
    { value: 'pan_right', label: 'Pan Right', desc: 'Move from left to right' },
    { value: 'pan_up', label: 'Pan Up', desc: 'Move from bottom to top' },
    { value: 'pan_down', label: 'Pan Down', desc: 'Move from top to bottom' },
    { value: 'diagonal', label: 'Diagonal', desc: 'Zoom + diagonal pan' }
  ];

  const handleSubmit = async () => {
    if (!image) {
      toast.error('Please select an image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', image);
      if (audio) formData.append('audio', audio);
      formData.append('duration', duration.toString());
      formData.append('effect', effect);
      formData.append('intensity', intensity.toString());

      const response = await fetch(`${API_URL}/api/video-studio/ken-burns`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to apply effect');
      }

      const result = await response.json();
      toast.success('Ken Burns effect applied!');
      onComplete?.(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-lg border border-orange-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Move className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-medium text-orange-300">Ken Burns Effect</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Add cinematic pan/zoom animation to still images.
        </p>
      </div>

      {/* Image Upload */}
      <div className="space-y-2">
        <Label className="text-white">Image</Label>
        <div 
          className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center cursor-pointer hover:border-zinc-500"
          onClick={() => document.getElementById('kenburns-image')?.click()}
        >
          {imagePreview ? (
            <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
          ) : (
            <div className="py-6">
              <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
              <p className="text-sm text-zinc-400">Click to upload</p>
            </div>
          )}
        </div>
        <input
          id="kenburns-image"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setImage(file);
              setImagePreview(URL.createObjectURL(file));
            }
          }}
        />
      </div>

      {/* Effect Selection */}
      <div className="space-y-2">
        <Label className="text-white">Effect Type</Label>
        <div className="grid grid-cols-2 gap-2">
          {effects.map((e) => (
            <button
              key={e.value}
              onClick={() => setEffect(e.value)}
              className={`p-2 rounded-lg border text-left transition-all ${
                effect === e.value
                  ? 'border-orange-500 bg-orange-500/20'
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
              }`}
            >
              <div className="text-sm font-medium text-white">{e.label}</div>
              <div className="text-xs text-zinc-400">{e.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Intensity */}
      <div className="space-y-1">
        <Label className="text-white text-sm">Intensity: {Math.round(intensity * 100)}%</Label>
        <Slider
          value={[intensity]}
          onValueChange={(v) => setIntensity(v[0])}
          min={0.1}
          max={0.5}
          step={0.05}
        />
      </div>

      {/* Duration */}
      <div className="space-y-1">
        <Label className="text-white text-sm">Duration: {duration}s</Label>
        <Slider
          value={[duration]}
          onValueChange={(v) => setDuration(v[0])}
          min={3}
          max={30}
          step={1}
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={loading || !image}
        className="w-full bg-gradient-to-r from-orange-500 to-amber-500"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Move className="w-4 h-4 mr-2" />}
        Apply Ken Burns
      </Button>
    </div>
  );
}

// ============================================================
// 5. AI IMAGE ANIMATOR COMPONENT
// ============================================================
export function AIImageAnimator({ onComplete, onClose }) {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [motionType, setMotionType] = useState('auto');
  const [duration, setDuration] = useState(5);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const getAuthToken = () => localStorage.getItem('auth_token');

  const motionTypes = [
    { value: 'auto', label: 'Auto', desc: 'AI decides best motion' },
    { value: 'subtle', label: 'Subtle', desc: 'Gentle, minimal movement' },
    { value: 'dynamic', label: 'Dynamic', desc: 'Active, noticeable motion' },
    { value: 'cinematic', label: 'Cinematic', desc: 'Camera movement effects' }
  ];

  const handleSubmit = async () => {
    if (!image) {
      toast.error('Please select an image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', image);
      formData.append('prompt', prompt);
      formData.append('motion_type', motionType);
      formData.append('duration', duration.toString());

      const response = await fetch(`${API_URL}/api/video-studio/ai-animate-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start animation');
      }

      const result = await response.json();
      setJobId(result.job_id);
      toast.info('AI animation started...');
      
      // Start polling
      pollJobStatus(result.job_id);
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const pollJobStatus = async (id) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/video-studio/job/${id}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        
        const job = await response.json();
        setProgress(job.progress || 0);
        setProgressMessage(job.progress_message || '');

        if (job.status === 'completed') {
          clearInterval(interval);
          setLoading(false);
          toast.success('Animation complete!');
          onComplete?.(job.result);
        } else if (job.status === 'failed') {
          clearInterval(interval);
          setLoading(false);
          toast.error(job.error || 'Animation failed');
        }
      } catch (error) {
        clearInterval(interval);
        setLoading(false);
      }
    }, 3000);
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-gradient-to-r from-purple-500/10 to-violet-500/10 rounded-lg border border-purple-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Wand2 className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium text-purple-300">AI Image Animator</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Use AI to bring still images to life with natural motion.
        </p>
      </div>

      {!loading ? (
        <>
          {/* Image Upload */}
          <div 
            className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center cursor-pointer hover:border-zinc-500"
            onClick={() => document.getElementById('ai-animate-image')?.click()}
          >
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
            ) : (
              <div className="py-6">
                <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                <p className="text-sm text-zinc-400">Click to upload image</p>
              </div>
            )}
          </div>
          <input
            id="ai-animate-image"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setImage(file);
                setImagePreview(URL.createObjectURL(file));
              }
            }}
          />

          {/* Prompt */}
          <div className="space-y-2">
            <Label className="text-white">Motion Description (Optional)</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the motion you want... e.g., 'Gentle breeze blowing through hair'"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* Motion Type */}
          <div className="grid grid-cols-2 gap-2">
            {motionTypes.map((m) => (
              <button
                key={m.value}
                onClick={() => setMotionType(m.value)}
                className={`p-2 rounded-lg border text-left ${
                  motionType === m.value
                    ? 'border-purple-500 bg-purple-500/20'
                    : 'border-zinc-700 bg-zinc-800/50'
                }`}
              >
                <div className="text-sm font-medium text-white">{m.label}</div>
                <div className="text-xs text-zinc-400">{m.desc}</div>
              </button>
            ))}
          </div>

          {/* Duration */}
          <div className="flex gap-2">
            <Button
              variant={duration === 5 ? "default" : "outline"}
              onClick={() => setDuration(5)}
              className="flex-1"
            >
              5 seconds
            </Button>
            <Button
              variant={duration === 10 ? "default" : "outline"}
              onClick={() => setDuration(10)}
              className="flex-1"
            >
              10 seconds
            </Button>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!image}
            className="w-full bg-gradient-to-r from-purple-500 to-violet-500"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Animate with AI
          </Button>
        </>
      ) : (
        <div className="text-center py-8 space-y-4">
          <div className="relative w-20 h-20 mx-auto">
            <div className="absolute inset-0 rounded-full bg-purple-500/30 animate-pulse" />
            <div className="absolute inset-2 rounded-full bg-zinc-900 flex items-center justify-center">
              <Wand2 className="w-8 h-8 text-purple-400 animate-pulse" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">AI is Animating...</h3>
            <p className="text-sm text-zinc-400">{progressMessage || 'This may take 2-5 minutes'}</p>
          </div>
          <Progress value={progress} className="w-full max-w-xs mx-auto" />
          <p className="text-xs text-zinc-500">{progress}%</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// 6. AUDIO WAVEFORM EDITOR COMPONENT  
// ============================================================
export function AudioWaveformEditor({ videoUrl, onComplete, onClose }) {
  const [waveform, setWaveform] = useState([]);
  const [keyframes, setKeyframes] = useState([
    { time: 0, volume: 1.0 }
  ]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [duration, setDuration] = useState(0);

  const getAuthToken = () => localStorage.getItem('auth_token');

  useEffect(() => {
    if (videoUrl) {
      analyzeWaveform();
    }
  }, [videoUrl]);

  const analyzeWaveform = async () => {
    setAnalyzing(true);
    try {
      const videoResponse = await fetch(videoUrl);
      const videoBlob = await videoResponse.blob();
      const videoFile = new File([videoBlob], 'video.mp4', { type: 'video/mp4' });

      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('samples', '100');

      const response = await fetch(`${API_URL}/api/video-studio/audio-waveform`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setWaveform(data.waveform);
        setDuration(data.duration);
        // Set initial keyframes at start and end
        setKeyframes([
          { time: 0, volume: 1.0 },
          { time: data.duration, volume: 1.0 }
        ]);
      }
    } catch (error) {
      console.error('Waveform analysis failed:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const addKeyframe = (time) => {
    const newKeyframes = [...keyframes, { time, volume: 1.0 }];
    setKeyframes(newKeyframes.sort((a, b) => a.time - b.time));
  };

  const updateKeyframe = (index, volume) => {
    const updated = [...keyframes];
    updated[index].volume = Math.max(0, Math.min(2, volume));
    setKeyframes(updated);
  };

  const removeKeyframe = (index) => {
    if (keyframes.length > 2) {
      setKeyframes(keyframes.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const videoResponse = await fetch(videoUrl);
      const videoBlob = await videoResponse.blob();
      const videoFile = new File([videoBlob], 'video.mp4', { type: 'video/mp4' });

      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('keyframes_json', JSON.stringify(keyframes));

      const response = await fetch(`${API_URL}/api/video-studio/audio-edit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to edit audio');
      }

      const result = await response.json();
      toast.success('Audio edited successfully!');
      onComplete?.(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-lg border border-cyan-500/20">
        <div className="flex items-center gap-2 mb-2">
          <AudioLines className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium text-cyan-300">Audio Editor</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Add volume keyframes to control audio levels throughout the video.
        </p>
      </div>

      {/* Waveform Visualization */}
      <div className="bg-zinc-800 rounded-lg p-4">
        {analyzing ? (
          <div className="flex items-center justify-center h-20">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
            <span className="ml-2 text-sm text-zinc-400">Analyzing audio...</span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-end justify-between h-16 gap-px">
              {waveform.map((value, i) => (
                <div
                  key={i}
                  className="flex-1 bg-cyan-500/50 rounded-t"
                  style={{ height: `${value * 100}%` }}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-zinc-500">
              <span>0:00</span>
              <span>{Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Keyframes */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-white">Volume Keyframes</Label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => addKeyframe(duration / 2)}
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {keyframes.map((kf, index) => (
            <div key={index} className="flex items-center gap-2 bg-zinc-800 p-2 rounded">
              <Clock className="w-4 h-4 text-zinc-500" />
              <span className="text-xs text-zinc-400 w-12">
                {kf.time.toFixed(1)}s
              </span>
              <div className="flex-1">
                <Slider
                  value={[kf.volume]}
                  onValueChange={(v) => updateKeyframe(index, v[0])}
                  min={0}
                  max={2}
                  step={0.1}
                />
              </div>
              <span className="text-xs text-zinc-400 w-10">
                {Math.round(kf.volume * 100)}%
              </span>
              {keyframes.length > 2 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeKeyframe(index)}
                  className="h-6 w-6"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AudioLines className="w-4 h-4 mr-2" />}
        Apply Audio Changes
      </Button>
    </div>
  );
}

export default {
  ImageToVideo,
  VoiceoverRecorder,
  SlideshowBuilder,
  KenBurnsEffect,
  AIImageAnimator,
  AudioWaveformEditor
};
